import { Observable, BehaviorSubject, Subject, timer, of } from 'rxjs';
import { concatMap, repeatWhen, takeUntil } from 'rxjs/operators';

import { Injectable, OnDestroy } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';

import { SnAbilityService } from '@lib/ability';
import { SnCameraGrid } from '@lib/api/entities';
import { SnCameraGridsAPI, SnSharesAPI } from '@lib/api/services';
import { SnCurrentUser } from '@lib/auth';
import { SnShareUiService } from '@lib/share';

@Injectable()
export class SnCameraGridActionsService implements OnDestroy {
  public readonly canAdd: boolean;
  public readonly canChange: boolean;
  public readonly canDelete: boolean;

  public readonly canShare: boolean;
  public readonly canDeleteShare: boolean;

  private readonly _destroy$ = new Subject<void>();
  private readonly _startTimer$ = new Subject<void>();
  private readonly _stopTimer$ = new Subject<void>();

  private mills$ = new BehaviorSubject<number>(0);
  private sec$ = new BehaviorSubject<number>(0);

  get time$(): Observable<number> {
    return this.mills$.asObservable();
  }
  get timeInSec$(): Observable<number> {
    return this.sec$.asObservable();
  }

  constructor(
    private readonly _activatedRoute: ActivatedRoute,
    private readonly _router: Router,
    private readonly _sharesAPI: SnSharesAPI,
    private readonly _shareUiService: SnShareUiService,
    private readonly _cameraGridsAPI: SnCameraGridsAPI,
    private readonly _currentUser: SnCurrentUser,
    ability: SnAbilityService,
  ) {
    this.canAdd = ability.can({ subject: 'cameras/cameraview', action: 'add' });
    this.canChange = ability.can({ subject: 'cameras/cameraview', action: 'change' });
    this.canDelete = ability.can({ subject: 'cameras/cameraview', action: 'delete' });

    const shareStatus = 'sharing/share';
    this.canShare = ability.can({
      subject: shareStatus,
      action: ['add', 'change', 'delete', 'view'],
    });
    this.canDeleteShare = ability.can({ subject: shareStatus, action: 'delete' });
  }

  public ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  public view(id: string): void {
    window.open(`/camera-grids/${id}`, '_blank');
  }

  public operationalView(id: string): void {
    window.open(`/camera-grids/operational-grid/${id}`, '_blank');
  }

  public edit(cameraGrid: SnCameraGrid): void {
    this._router.navigate(['/camera-grids', cameraGrid.id, 'edit']);
  }

  public create(): void {
    this._router.navigate(['/camera-grids', 'create']);
  }

  public delete(grid: SnCameraGrid): Observable<Object> {
    const delete$ = grid.isOwned
      ? this._cameraGridsAPI.deleteCameraGrid(grid.id)
      : this._sharesAPI.deleteShare(grid.shareInfo.id);

    return delete$.pipe(takeUntil(this._destroy$));
  }

  public share(grid: SnCameraGrid): Observable<SnCameraGrid> {
    return this._shareUiService
      .shareSettings({
        object: grid,
        handlers: this._cameraGridsAPI,
      })
      .afterClosed$.pipe(
        concatMap(() => this._cameraGridsAPI.getCameraGrid(grid.id)),
        takeUntil(this._destroy$),
      );
  }

  public search(value: string): void {
    this._router.navigate([], {
      relativeTo: this._activatedRoute,
      queryParamsHandling: 'merge',
      queryParams: {
        search: value,
      },
    });
  }

  public setTimerSlideShow(timer: number): void {
    this.sec$.next(timer);
    this.mills$.next(timer * 1000);
  }

  public initTimeoutTimer(): Observable<number> {
    if (this._currentUser.isVideoWallUser) {
      return of(null);
    }
    return timer(0, 1000).pipe(
      takeUntil(this._stopTimer$),
      takeUntil(this._destroy$),
      repeatWhen(() => this._startTimer$),
    );
  }

  public startTimer(): void {
    this._startTimer$.next();
  }

  public stopTimer(): void {
    this._stopTimer$.next();
  }
}
